'use client';
import { useState, useEffect, Suspense } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Accordion from '@/components/ui/Accordion';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Link from 'next/link';

function PricingPageInner() {
  const [activePhase2, setActivePhase2] = useState<'retainer' | 'buildout' | 'hybrid'>('retainer');

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [activePhase2]);

  const faqItems = [
    {
      id: 'faq-1',
      title: 'Do I have to commit to a monthly retainer?',
      content: 'No. Every engagement starts with a Sprint — a fixed-fee diagnostic that gives you a clear picture of where to invest. After the Sprint, you choose the path that fits: a scoped project, an ongoing execution partnership, or just the roadmap to run yourself. There is no pressure to fit a monthly box.'
    },
    {
      id: 'faq-2',
      title: 'What does the Sprint actually deliver?',
      content: 'The Sprint is a paid diagnostic Sprint to find the bottlenecks and build the roadmap. Then we scope the right path forward — a project, retainer, or hybrid. Pricing reflects what we are actually solving, not just time on the clock.'
    },
    {
      id: 'faq-3',
      title: 'What are the three paths after the Sprint?',
      content: 'After the Sprint, you have three options. First, roadmap only — you take the deliverables and execute internally. Second, project implementation — we scope and build out the specific system, funnel, or infrastructure the Sprint uncovered. Third, ongoing execution partnership — we stay on as your growth team, executing strategy, content, campaigns, and optimization every month. Most clients choose based on what the Sprint reveals.'
    },
    {
      id: 'faq-4',
      title: 'How is project pricing determined?',
      content: 'Project pricing is scoped based on what the Sprint uncovers. A focused funnel build might be $10k–$25k. A full infrastructure buildout — new positioning, content system, paid media setup, automation — can run $50k–$75k or more. We price based on scope, complexity, and the business value of the outcome — not just hours.'
    },
    {
      id: 'faq-5',
      title: 'What does the monthly retainer include?',
      content: 'The retainer is a fluid ongoing execution partnership — not a fixed deliverable list. We handle strategy, content, campaigns, optimization, reporting, and whatever the business needs that month. Depth and access vary by tier. Engagements typically run $3k–$10k+/month depending on how much execution you need and how involved you want us to be.'
    },
    {
      id: 'faq-6',
      title: 'What is the DIY Portal?',
      content: 'The DIY Portal is a lower-ticket recurring layer for founders who want our systems, frameworks, templates, prompt packs, dashboards, and SOPs — but want to run execution themselves. It is not a course. It is the actual operating infrastructure we use, handed to you with support. Pricing runs $500–$1,500/month depending on access level.'
    },
    {
      id: 'faq-7',
      title: 'How do you price — is it hourly?',
      content: 'No. We do not bill hourly. Pricing reflects scope, speed, and business value — not time. Sprints are fixed-fee. Projects are scoped and priced upfront. Retainers are monthly agreements based on depth of execution. The DIY Portal is a flat subscription. You always know what you are paying before we start.'
    },
    {
      id: 'faq-8',
      title: 'What size businesses do you work with?',
      content: 'The Sprint entry point starts at $2,500 for smaller businesses and scales to $15k+ for larger or more established operations. Project and retainer engagements are typically a fit for businesses doing $500k–$5M+ in revenue with real momentum and operational bottlenecks. If you are pre-revenue or just starting out, the DIY Portal is the right starting point.'
    }
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">

        {/* Hero */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Pricing That Reflects Scope, Speed, and Business Value
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                Not every client fits
                <br />
                <span className="text-primary">a flat monthly box.</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
                We start with a paid diagnostic Sprint to find the bottlenecks and build the roadmap. Then we scope the right path forward — a project, retainer, or hybrid. Pricing reflects what we are actually solving, not just time on the clock.
              </p>
              <p className="text-body-sm text-muted-foreground max-w-xl mx-auto mb-10">
                Every engagement is different. Every price is earned.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book">
                  <Button variant="primary" size="lg" className="border-glow">
                    Start With a Sprint
                  </Button>
                </Link>
                <Link href="/contact">
                  <Button variant="secondary" size="lg">
                    Talk to Us First
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* The Path */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                How It Works
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                The natural path forward
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Most clients follow the same arc. Start with clarity. Then build or execute based on what the data shows.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 mb-12">
              {[
                { step: '01', label: 'Sprint', desc: 'Paid diagnostic. Audit, bottleneck analysis, roadmap, priorities.' },
                { step: '02', label: 'Decide', desc: 'Three paths: roadmap only, project build, or ongoing execution.' },
                { step: '03', label: 'Execute', desc: 'Scoped project, retainer, or hybrid — priced for what you actually need.' },
                { step: '04', label: 'Scale', desc: 'Ongoing optimization, tools, and systems that compound over time.' },
              ].map((item, i) => (
                <div key={i} className="reveal relative p-8 border border-border bg-background">
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-px w-px h-full bg-border z-10" />
                  )}
                  <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-3 block">{item.step}</span>
                  <h3 className="text-h4 mb-3">{item.label}</h3>
                  <p className="text-body-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="reveal max-w-3xl mx-auto p-6 border border-primary bg-primary/5 text-center">
              <p className="text-body-sm text-muted-foreground">
                <strong className="text-white">The Sprint is not a sales call.</strong> It is a paid engagement that delivers real value — audit findings, bottleneck analysis, a prioritized roadmap, and quick wins. You own the output regardless of what comes next.
              </p>
            </div>
          </div>
        </section>

        {/* Phase 1: Sprint */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="Phase 1 — Sprint / Diagnostic"
                title="Start here. Every time."
                description="Before we build anything, we need to know what is actually broken. The Sprint is a fixed-fee strategic diagnostic that gives you a clear picture of your bottlenecks, priorities, and the highest-leverage moves available to your business right now."
                align="center"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* What's included */}
              <div className="reveal p-10 border border-border bg-surface-1">
                <h3 className="text-h4 mb-6">What the Sprint delivers</h3>
                <ul className="space-y-4">
                  {[
                    { title: 'Full business & marketing audit', desc: 'We review your current operations, positioning, channels, and conversion points.' },
                    { title: 'Bottleneck analysis', desc: 'We identify exactly where revenue is leaking and where execution is breaking down.' },
                    { title: 'Prioritized roadmap', desc: 'A clear, sequenced plan of what to fix first, second, and third — and why.' },
                    { title: 'Channel & execution recommendations', desc: 'Where to invest, what to cut, and what to double down on.' },
                    { title: 'Quick wins', desc: 'Where appropriate, we move fast on low-effort, high-impact improvements during the Sprint itself.' },
                    { title: 'Post-Sprint path recommendation', desc: 'A clear recommendation on whether a project, retainer, or hybrid makes sense for your situation.' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="text-primary mt-1 shrink-0">✓</span>
                      <div>
                        <span className="text-white font-semibold text-body-sm">{item.title}</span>
                        <p className="text-body-sm text-muted-foreground mt-1">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing tiers */}
              <div className="reveal space-y-6">
                <h3 className="text-h4 mb-2">Sprint Pricing</h3>
                <p className="text-body-sm text-muted-foreground mb-6">
                  Fixed fee. Scoped to your business size and complexity. No surprises.
                </p>

                <div className="p-8 border border-border bg-background hover:border-primary transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge variant="secondary" className="mb-3">Smaller Businesses</Badge>
                      <h4 className="text-h4">Entry Sprint</h4>
                    </div>
                    <div className="text-right">
                      <span className="text-h2 text-primary">$2,500</span>
                      <p className="text-body-sm text-muted-foreground">fixed fee</p>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground mb-6">
                    For early-stage or smaller businesses that need clarity before committing to a larger engagement. Full audit, roadmap, and recommendations — scoped for your stage.
                  </p>
                  <Link href="/book">
                    <Button variant="secondary" size="md" className="w-full">Book Entry Sprint</Button>
                  </Link>
                </div>

                <div className="p-8 border border-primary bg-primary/5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge variant="primary" className="mb-3">Most Common</Badge>
                      <h4 className="text-h4">Standard Sprint</h4>
                    </div>
                    <div className="text-right">
                      <span className="text-h2 text-primary">$5k–$10k</span>
                      <p className="text-body-sm text-muted-foreground">fixed fee</p>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground mb-6">
                    For businesses with revenue and momentum that need a thorough diagnostic. Full audit, deep bottleneck analysis, prioritized roadmap, and a clear post-Sprint path recommendation.
                  </p>
                  <Link href="/book">
                    <Button variant="primary" size="md" className="w-full">Book Standard Sprint</Button>
                  </Link>
                </div>

                <div className="p-8 border border-border bg-background hover:border-primary transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge variant="secondary" className="mb-3">Larger / Established</Badge>
                      <h4 className="text-h4">Enterprise Sprint</h4>
                    </div>
                    <div className="text-right">
                      <span className="text-h2 text-primary">$15k+</span>
                      <p className="text-body-sm text-muted-foreground">fixed fee</p>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground mb-6">
                    For larger, more established businesses with complex operations, multiple revenue streams, or significant infrastructure. Deeper engagement, broader scope, more stakeholders.
                  </p>
                  <Link href="/book">
                    <Button variant="secondary" size="md" className="w-full">Book Enterprise Sprint</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Post-Sprint Paths */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                After the Sprint
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Three paths. One right answer for your situation.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                The Sprint tells us what to do. These paths determine how we do it — or whether you take the roadmap and run it yourself.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  number: 'Path A',
                  title: 'Roadmap Only',
                  desc: 'You take the Sprint deliverables — the audit, the bottleneck analysis, the roadmap — and execute internally. No ongoing engagement required. Best for teams with strong internal execution capacity who just needed the strategic clarity.',
                  cta: 'Start With a Sprint',
                  href: '/book',
                  highlight: false,
                },
                {
                  number: 'Path B',
                  title: 'Project Implementation',
                  desc: 'The Sprint uncovered a clear buildout — a new funnel, a content system, a paid media infrastructure, an automation stack. We scope it, price it, and build it. Fixed scope, fixed price, defined deliverables.',
                  cta: 'Discuss a Project',
                  href: '/contact',
                  highlight: false,
                },
                {
                  number: 'Path C',
                  title: 'Ongoing Execution Partnership',
                  desc: 'You want us to stay on as your growth team — executing strategy, content, campaigns, and optimization every month. Fluid, adaptive, and built around what the business actually needs each month.',
                  cta: 'Explore Retainer',
                  href: '/book',
                  highlight: false,
                },
              ].map((path, i) => (
                <div key={i} className={`reveal p-10 border ${path.highlight ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}>
                  <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-3 block">{path.number}</span>
                  <h3 className="text-h3 mb-4">{path.title}</h3>
                  <p className="text-body-sm text-muted-foreground mb-8 leading-relaxed">{path.desc}</p>
                  <Link href={path.href}>
                    <Button variant={path.highlight ? 'primary' : 'secondary'} size="md" className="w-full">{path.cta}</Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Phase 2: Implementation */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="Phase 2 — Implementation"
                title="Priced for what you're actually building."
                description="If the Sprint uncovers $50k–$250k worth of work, the pricing should reflect that. We scope every project based on what it will actually take — and what it is worth to your business."
                align="center"
              />
            </div>

            {/* Tab Nav */}
            <div className="reveal flex flex-wrap justify-center gap-2 mb-16">
              {[
                { key: 'retainer', label: 'Monthly Retainer' },
                { key: 'buildout', label: 'Fixed-Scope Project' },
                { key: 'hybrid', label: 'Hybrid Model' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActivePhase2(tab.key as typeof activePhase2)}
                  className={`px-8 py-4 text-label uppercase tracking-widest font-semibold transition-all duration-300 ${
                    activePhase2 === tab.key
                      ? 'bg-primary text-primary-foreground border-2 border-primary'
                      : 'bg-surface-1 text-muted-foreground border-2 border-border hover:border-primary hover:text-foreground cursor-pointer'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Retainer */}
            {activePhase2 === 'retainer' && (
              <div className="reveal max-w-4xl mx-auto">
                <div className="p-10 border border-border bg-surface-1 mb-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                    <div>
                      <Badge variant="primary" className="mb-4">Option A</Badge>
                      <h3 className="text-h3 mb-3">Monthly Retainer</h3>
                      <p className="text-body text-muted-foreground max-w-xl">
                        Fluid, ongoing execution. We stay on as your growth team — adapting to what the business needs each month. Strategy, content, campaigns, optimization, reporting. No fixed deliverable list. Just execution that moves the needle.
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-h2 text-primary">$3k–$10k+</span>
                      <p className="text-body-sm text-muted-foreground">/month</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {[
                      { title: 'Advisory & Strategy', desc: 'Monthly planning, prioritization, and decision support.' },
                      { title: 'Content & Creative Execution', desc: 'Copy, creative, campaigns — built and shipped every month.' },
                      { title: 'Paid Media Management', desc: 'Campaign setup, optimization, and reporting across channels.' },
                      { title: 'Automation & Systems', desc: 'Email sequences, follow-up flows, and operational improvements.' },
                      { title: 'Performance Reporting', desc: 'Monthly reporting with insights, not just numbers.' },
                      { title: 'Ongoing Optimization', desc: 'We test, iterate, and improve based on what the data shows.' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <span className="text-primary mt-1 shrink-0">✓</span>
                        <div>
                          <span className="text-white font-semibold text-body-sm">{item.title}</span>
                          <p className="text-body-sm text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-6 border border-border bg-background mb-8">
                    <p className="text-body-sm text-muted-foreground">
                      <strong className="text-white">Pricing is based on depth and access.</strong> A focused advisory retainer with light execution runs $3k–$5k/month. Full execution partnership with dedicated team capacity runs $7k–$10k+/month. We scope based on what the Sprint uncovered and what the business actually needs.
                    </p>
                  </div>
                  <Link href="/book">
                    <Button variant="primary" size="lg" className="w-full md:w-auto">Discuss a Retainer</Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Buildout */}
            {activePhase2 === 'buildout' && (
              <div className="reveal max-w-4xl mx-auto">
                <div className="p-10 border border-primary bg-primary/5 mb-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                    <div>
                      <Badge variant="primary" className="mb-4">Option B</Badge>
                      <h3 className="text-h3 mb-3">Fixed-Scope Project</h3>
                      <p className="text-body text-muted-foreground max-w-xl">
                        When the Sprint uncovers a clear buildout — a new funnel, a content infrastructure, a paid media system, a full positioning overhaul — we scope it, price it, and build it. Fixed scope. Fixed price. Defined deliverables. No ambiguity.
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-h2 text-primary">$10k–$75k+</span>
                      <p className="text-body-sm text-muted-foreground">per project</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {[
                      { title: 'Scoped from Sprint findings', desc: 'Every project is defined by what the diagnostic actually uncovered — not a generic package.' },
                      { title: 'Fixed price, defined deliverables', desc: 'You know exactly what you are getting and what it costs before we start.' },
                      { title: 'Clear timeline', desc: 'Projects have defined start and end dates with milestone checkpoints.' },
                      { title: 'Priced for business value', desc: 'We price based on what the outcome is worth — not just hours.' },
                      { title: 'Handoff and documentation', desc: 'Everything we build is documented and handed off so you can run it.' },
                      { title: 'Optional ongoing support', desc: 'After delivery, you can add a maintenance retainer or move to ongoing execution.' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <span className="text-primary mt-1 shrink-0">✓</span>
                        <div>
                          <span className="text-white font-semibold text-body-sm">{item.title}</span>
                          <p className="text-body-sm text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-6 border border-primary/30 bg-background mb-8">
                    <p className="text-body-sm text-muted-foreground">
                      <strong className="text-white">Example project ranges:</strong> A focused funnel build runs $10k–$25k. A full content and paid media infrastructure buildout runs $25k–$50k. A comprehensive growth infrastructure — positioning, systems, automation, and campaigns — can run $50k–$75k+. Scope determines price.
                    </p>
                  </div>
                  <Link href="/contact">
                    <Button variant="primary" size="lg" className="w-full md:w-auto">Discuss a Project</Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Hybrid */}
            {activePhase2 === 'hybrid' && (
              <div className="reveal max-w-4xl mx-auto">
                <div className="p-10 border border-border bg-surface-1 mb-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                    <div>
                      <Badge variant="secondary" className="mb-4">Option C</Badge>
                      <h3 className="text-h3 mb-3">Hybrid Model</h3>
                      <p className="text-body text-muted-foreground max-w-xl">
                        An upfront build fee to get the infrastructure in place, followed by a monthly support and optimization retainer to run and improve it. Best when the Sprint uncovers a clear buildout need but the business also wants ongoing execution after delivery.
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-h2 text-primary">Build + Retainer</span>
                      <p className="text-body-sm text-muted-foreground">custom scoped</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="p-6 border border-border bg-background">
                      <h4 className="text-h5 mb-4 text-primary">Phase 1 — Build Fee</h4>
                      <p className="text-body-sm text-muted-foreground mb-4">Fixed-scope project to build the infrastructure. Scoped from Sprint findings. Defined deliverables, timeline, and price.</p>
                      <p className="text-body-sm text-white font-semibold">Typically $10k–$50k+ depending on scope</p>
                    </div>
                    <div className="p-6 border border-border bg-background">
                      <h4 className="text-h5 mb-4 text-primary">Phase 2 — Monthly Support</h4>
                      <p className="text-body-sm text-muted-foreground mb-4">Ongoing optimization, management, and execution after the build is live. Lighter than a full retainer — focused on running and improving what we built.</p>
                      <p className="text-body-sm text-white font-semibold">Typically $2k–$5k/month</p>
                    </div>
                  </div>
                  <div className="p-6 border border-border bg-background mb-8">
                    <p className="text-body-sm text-muted-foreground">
                      <strong className="text-white">Best for:</strong> Businesses that need a significant infrastructure buildout but also want ongoing support to run it after launch. The build fee gets the system in place. The retainer keeps it optimized and growing.
                    </p>
                  </div>
                  <Link href="/contact">
                    <Button variant="primary" size="lg" className="w-full md:w-auto">Discuss a Hybrid Engagement</Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* DIY Portal */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="DIY / Portal / Subscription"
                title="Our systems. Your hands."
                description="For founders who want to run execution themselves — with the actual frameworks, templates, and tools we use for clients. Not a course. Not a PDF. The real operating infrastructure."
                align="center"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {[
                {
                  name: 'Portal Starter',
                  price: '$500',
                  period: '/month',
                  desc: 'Core templates, frameworks, and SOPs. The foundation for running your own growth system.',
                  features: [
                    'Full templates library',
                    'Prompt packs & AI frameworks',
                    'Core SOPs and playbooks',
                    'Monthly group Q&A call',
                    'Community access',
                  ],
                  highlight: false,
                  cta: 'Join Portal Starter',
                },
                {
                  name: 'Portal Growth',
                  price: '$1,000',
                  period: '/month',
                  desc: 'Everything in Starter plus dashboards, advanced systems, and async support for faster execution.',
                  features: [
                    'Everything in Starter',
                    'Performance dashboards',
                    'Advanced automation frameworks',
                    'Bi-weekly strategy call',
                    'Async Slack support',
                    'Campaign review & feedback',
                  ],
                  highlight: true,
                  cta: 'Join Portal Growth',
                },
                {
                  name: 'Portal Pro',
                  price: '$1,500',
                  period: '/month',
                  desc: 'Maximum access. Weekly calls, hands-on help, and the closest thing to Concierge without handing over the keys.',
                  features: [
                    'Everything in Growth',
                    'Weekly 60-min strategy call',
                    'Up to 3 hours hands-on help/month',
                    'Priority support',
                    'Quarterly roadmap planning',
                    'First access to new frameworks',
                  ],
                  highlight: false,
                  cta: 'Join Portal Pro',
                },
              ].map((tier, i) => (
                <div key={i} className={`reveal p-8 border ${tier.highlight ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}>
                  {tier.highlight && <Badge variant="primary" className="mb-4">Most Popular</Badge>}
                  <h3 className="text-h4 mb-2">{tier.name}</h3>
                  <p className="text-body-sm text-muted-foreground mb-6">{tier.desc}</p>
                  <div className="mb-6">
                    <span className="text-h2 text-primary">{tier.price}</span>
                    <span className="text-body text-muted-foreground">{tier.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3 text-body-sm">
                        <span className="text-primary mt-1 shrink-0">✓</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link href="/diy">
                    <Button variant={tier.highlight ? 'primary' : 'secondary'} size="md" className="w-full">{tier.cta}</Button>
                  </Link>
                </div>
              ))}
            </div>

            <div className="reveal max-w-3xl mx-auto p-6 border border-border bg-background text-center">
              <p className="text-body-sm text-muted-foreground">
                <strong className="text-white">Outgrowing the Portal?</strong> Most founders who hit consistent growth on Portal Pro eventually realize their time is worth more than the execution savings. When you are ready to stop doing it yourself,{' '}
                <Link href="/book" className="text-primary underline underline-offset-2 hover:text-primary-hover transition-colors font-medium">
                  start with a Sprint →
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Philosophy */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="Our Pricing Philosophy"
                title="We price for value, not time."
                description="Every engagement is different. Every price should reflect what we are actually solving — not a generic package that forces every client into the same box."
                align="center"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: '📐',
                  title: 'Scope',
                  desc: 'What are we actually building? A focused funnel is not the same as a full growth infrastructure. Scope determines price.',
                },
                {
                  icon: '⚡',
                  title: 'Speed',
                  desc: 'How fast do you need it? Compressed timelines require more resources. We price accordingly — and we are honest about what speed costs.',
                },
                {
                  icon: '📈',
                  title: 'Business Value',
                  desc: 'If the work unlocks $250k in new revenue, the price should reflect that. We are not charging for hours. We are charging for outcomes.',
                },
              ].map((item, i) => (
                <div key={i} className="reveal p-8 border border-border bg-surface-1">
                  <div className="w-12 h-12 bg-primary/10 border border-primary flex items-center justify-center mb-6">
                    <span className="text-primary text-h4">{item.icon}</span>
                  </div>
                  <h3 className="text-h4 mb-3">{item.title}</h3>
                  <p className="text-body-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-16">
              <SectionHeader
                eyebrow="Questions?"
                title="Straight answers to real questions."
                description="No marketing fluff. Here is exactly how our pricing and process works."
                align="center"
              />
            </div>
            <div className="max-w-3xl mx-auto reveal">
              <Accordion items={faqItems} allowMultiple={true} />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto text-center">
            <div className="reveal">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                The Sprint is where everything starts.
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Get clarity before you commit.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Book a call and we will walk you through the Sprint process, what it delivers, and what path makes sense for your business. No pressure. No pitch. Just a straight conversation about where you are and where you want to go.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book">
                  <Button variant="primary" size="lg" className="border-glow">
                    Book a Call
                  </Button>
                </Link>
                <Link href="/contact">
                  <Button variant="secondary" size="lg">
                    Send Us a Message
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}

export default function PricingPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <PricingPageInner />
    </Suspense>
  );
}